import { ValidationResult, ValidationRule, FieldType, FieldValidation } from './types';
import { validateSSN } from '@gjensidige/se-helpers';
import Personnummer from 'personnummer';

export const ageResolver = (ssn: string) => Personnummer.parse(ssn).getAge();

const PATTERNS: Record<FieldType | 'ssnAge', RegExp | ((value: string, params?: any) => boolean)> = {
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, //TODO: Check if regex is correct
  zipCode: /^\d{5}$/,
  ssn: (value: string) => {
    return (value.length === 10 || value.length === 12) && validateSSN(value)
  },
  regNr: (value: string) => {
    return /([A-Z]{3})([0-9]{2})([0-9A-Z])/.test(value) && value.length === 6;
  },
  address: (value: string) => {
    return value.length >= 3 && value.length <= 40;
  },
  propertyKey: (value: string) => {
    if (value.length > 50 && value.length <= 3) {
      return false;
    }
    const propertyNameNoWhitespace = value.replace(/\s/g, '');
    const regex = /^[a-zA-ZåäöÅÄÖ.,-]+\d+([:,.]?[a-zA-ZåäöÅÄÖ.,-:\d]+)?$/;
    return regex.test(propertyNameNoWhitespace);
  },
  municipality: (value: string) => {
    return value.length >= 3 && value.length <= 40;
  },
  phone: (value: string) => {
    if (!/^[0-9]*$/.test(value) || value.length !== 10) {
      return false;
    }
    return true;
  },
  ssnAge: (value: string, params?: any) => {
    if (!validateSSN(value)) return false;
    if (params && params.minAge !== undefined) {
      const age = ageResolver(value);
      const minAge = params.minAge;
      return age >= minAge;
    }
    return true;
  },
};

const ERROR_MESSAGES: Record<ValidationRule, ((fieldName: string, params?: any) => string)> = {
  required: (fieldName: string) => `${fieldName} måste fyllas i`,
  email: () => 'E-postadressen har fel format.',
  phone: () => 'Numret har fel format, ange 10 siffror utan bindestreck eller mellanslag.',
  ssn: () => 'Personnumret har fel format, ange 12 siffror i formatet ÅÅÅÅMMDDNNNN.',
  zipCode: () => 'Postnumret har fel format.',
  regNr: () => 'Registreringsnumret har fel format.',
  address: () => 'Adressen har fel format.',
  propertyKey: () => 'Fastighetsbeteckningen har fel format.',
  municipality: () => 'Kommunnamnet har fel format.',
  ssnAge: (fieldName: string, params?: any) => {
    const minAge = params?.minAge;
    return `Du måste vara över ${minAge} år för att köpa en försäkring`;
  },
} as const;

export const validateField = (
  value: string,
  rules: FieldValidation['rules'],
  fieldName: FieldValidation['fieldName'],
  customMessage?: FieldValidation['customMessage'],
  validationParams?: FieldValidation['validationParams']
): ValidationResult => {
  if (rules.includes('required') && (!value || value.trim() === '')) {
    return {
      isValid: false,
      errorMessage: customMessage || ERROR_MESSAGES.required(fieldName)
    };
  }

  if (!value || value.trim() === '') {
    return { isValid: true };
  }

  for (const rule of rules) {
    if (rule === 'required') continue;

    if (rule in PATTERNS) {
      const pattern = PATTERNS[rule as keyof typeof PATTERNS];
      let isValid = false;

      if (typeof pattern === 'function') {
        isValid = pattern(value, validationParams);
      } else {
        isValid = pattern.test(value);
      }

      if (!isValid) {
        const errorMessageKey = rule as keyof typeof ERROR_MESSAGES;

        return {
          isValid: false,
          errorMessage: customMessage || (ERROR_MESSAGES[errorMessageKey] ? ERROR_MESSAGES[errorMessageKey](fieldName, validationParams) : `Invalid ${fieldName}`)
        };
      }
    }
  }

  return { isValid: true };
};